/**
 * Proposal rows → domain entities → everything the phone's proposal screen
 * renders (docs/01 §4).
 *
 * Resolution, quorum arithmetic, the money lock, counter-proposals and the
 * decision log all come from `@office/domain/proposals`. This file only feeds
 * it rows and shapes the answers. In particular:
 *
 *  - **A proposal's status is recomputed, not read.** `settle` is run over the
 *    mirror on every render, so a proposal whose 4-hour deadline passed while
 *    the phone was in a pocket shows as approved without waiting for the
 *    server's sweep to sync back.
 *  - **Money never auto-approves.** The live type registry is passed into every
 *    `settle` / `summarise` call, so even a corrupted row carrying a stray
 *    `auto_approve_at` on a purchase is ignored rather than obeyed.
 */

import { useCallback, useMemo, useState } from 'react'
import type { OfficeDb } from '../../lib/db.js'
import { mutations } from '../../lib/mutations.js'
import {
  acceptCounterProposal,
  BUILT_IN_PROPOSAL_TYPE_REGISTRY,
  canCancel as domainCanCancel,
  cancel as domainCancel,
  counterProposalsOf,
  decisionLog,
  proposalTypesFromSettings,
  settle,
  summarise,
  supersededBy,
  type CounterProposal,
  type DecisionLogEvent,
  type ProposalSummary,
  type ProposalTypeRegistry,
} from '@office/domain/proposals'
import {
  uuidv7,
  type DecisionReason,
  type MemberId,
  type Proposal,
  type ProposalId,
  type ProposalStatus,
  type ProposalType,
  type ProposalVote,
  type Quorum,
  type TrustedClock,
  type Vote,
} from '@office/domain'
import {
  asBool,
  asIdOrNull,
  asMs,
  asNum,
  asRecord,
  asText,
  asTextOrNull,
  column,
  nameOf,
  nameOrYou,
  useMembers,
  useMutationContext,
  useNow,
  useSettings,
  useTable,
  type MemberLite,
  type Row,
} from '../chores/live.js'

// ---------------------------------------------------------------------------
// Row adapters
// ---------------------------------------------------------------------------

const STATUSES: readonly ProposalStatus[] = [
  'pending',
  'approved',
  'rejected',
  'expired',
  'cancelled',
]
const REASONS: readonly DecisionReason[] = [
  'unanimous_approval',
  'rejected_by_vote',
  'auto_approved_timeout',
  'cancelled_by_proposer',
  'expired',
]

export function asProposalStatus(v: unknown): ProposalStatus {
  const s = asText(v)
  return (STATUSES as readonly string[]).includes(s) ? (s as ProposalStatus) : 'pending'
}

export function asDecisionReason(v: unknown): DecisionReason | null {
  const s = asText(v)
  return (REASONS as readonly string[]).includes(s) ? (s as DecisionReason) : null
}

export function asVote(v: unknown): Vote {
  return asText(v) === 'reject' ? 'reject' : 'approve'
}

export function toProposal(row: Row): Proposal {
  return {
    id: asText(row['id']) as ProposalId,
    typeKey: asText(column(row, 'type_key'), 'generic'),
    createdBy: asText(column(row, 'created_by')) as MemberId,
    createdAt: asMs(column(row, 'created_at')) ?? 0,
    title: asText(row['title']),
    details: asTextOrNull(row['details']),
    payload: asRecord(row['payload']),
    status: asProposalStatus(row['status']),
    autoApproveAt: asMs(column(row, 'auto_approve_at')),
    reminderSentAt: asMs(column(row, 'reminder_sent_at')),
    decidedAt: asMs(column(row, 'decided_at')),
    decisionReason: asDecisionReason(column(row, 'decision_reason')),
    supersedesId: asIdOrNull<ProposalId>(column(row, 'supersedes_id')),
  }
}

export function toProposalVote(row: Row): ProposalVote {
  const counter = column(row, 'counter_proposal')
  return {
    proposalId: asText(column(row, 'proposal_id')) as ProposalId,
    voterId: asText(column(row, 'voter_id')) as MemberId,
    vote: asVote(row['vote']),
    comment: asTextOrNull(row['comment']),
    counterProposal:
      counter === null || counter === undefined || counter === '' ? null : asRecord(counter),
    votedAt: asMs(column(row, 'voted_at')) ?? 0,
  }
}

export function toProposalType(row: Row): ProposalType {
  const hours = column(row, 'auto_approve_after_hours')
  const key = asText(row['key'])
  const builtIn = BUILT_IN_PROPOSAL_TYPE_REGISTRY[key]
  /**
   * A mirrored row may add a money-related type, and may not take money off one
   * (docs/01 §4.1: money NEVER auto-approves). `proposalTypesFromSettings` lets
   * a stored type override a built-in wholesale, so a `proposal_types` row for
   * `purchase` that simply omits `is_money_related` — a partial sync payload, a
   * migration that dropped the column — would have read as `false`, unlocked
   * `autoApproveAfterHours`, and quietly turned the money lock into a
   * four-hour countdown. The built-in's answer is the floor; a row can only
   * ever add the flag, never remove it.
   */
  const isMoneyRelated = builtIn?.isMoneyRelated === true || asBool(column(row, 'is_money_related'))
  return {
    key,
    label: asText(row['label'], builtIn?.label ?? 'Proposal'),
    icon: asText(row['icon'], builtIn?.icon ?? 'message-square'),
    quorum: (asText(row['quorum'], 'unanimous') === 'majority' ? 'majority' : 'unanimous') as Quorum,
    autoApproveAfterHours:
      isMoneyRelated || hours === null || hours === undefined || hours === ''
        ? null
        : asNum(hours, 4),
    isMoneyRelated,
  }
}

// ---------------------------------------------------------------------------
// View models
// ---------------------------------------------------------------------------

export interface ProposalView {
  proposal: Proposal
  type: ProposalType | null
  label: string
  summary: ProposalSummary
  /** The viewer raised it. */
  mine: boolean
  /** The viewer is an eligible voter who has not voted. */
  awaitingMe: boolean
  /**
   * The viewer's vote is already recorded. Distinct from `!awaitingMe`, which
   * is also true for someone who is not an eligible voter at all — telling an
   * intern "you have already voted" is simply false (docs/01 §1).
   */
  votedByMe: boolean
  /** The viewer is one of the people whose tap this proposal is waiting for. */
  eligibleToVote: boolean
  canCancel: boolean
  /** Money proposals NEVER auto-approve (docs/01 §4.1). */
  money: boolean
  counters: CounterProposal[]
  proposerName: string
}

export interface Relaunchable {
  view: ProposalView
  counter: CounterProposal
  counterFrom: string
}

export interface ProposalBoard {
  /** Corrected office time. Never `Date.now()`. */
  now: number
  /**
   * The configured office day boundary (docs/01, the day-boundary rule). The
   * decision log dates entries with it, so a decision taken at 02:00 belongs to
   * the office day people were still working through, not to the calendar day
   * the clock had just rolled into.
   */
  rolloverHour: number
  members: readonly MemberLite[]
  eligible: MemberId[]
  pending: ProposalView[]
  decided: ProposalView[]
  /**
   * The viewer's own rejected proposals that came back with an alternative
   * nobody has raised yet. This is the answer to "a bare no leaves the
   * requester guessing" — the alternative lands on the requester's phone as a
   * one-tap action instead of a message they have to remember.
   */
  relaunchable: Relaunchable[]
  log: DecisionLogEvent[]
  types: ProposalTypeRegistry
}

export interface ProposalActions {
  approve(view: ProposalView): Promise<void>
  /** "No — try another time": a reject that carries a suggested alternative. */
  rejectWithAlternative(view: ProposalView, suggestion: string, note: string): Promise<void>
  /** "No — explain": a reject that must say why. */
  rejectWithReason(view: ProposalView, reason: string): Promise<void>
  cancel(view: ProposalView): Promise<void>
  /** Raise the counter as a NEW proposal, linked by supersedesId. */
  raise(view: ProposalView, counter: CounterProposal): Promise<void>
}

export interface UseProposalBoardOptions {
  nowIntervalMs?: number | undefined
}

/** The free-text field a counter-proposal carries. */
export const COUNTER_SUGGESTION_KEY = 'suggestion'

/** Human-readable rendering of a counter payload. */
export function describeCounter(counter: CounterProposal): string {
  const suggestion = counter.payload[COUNTER_SUGGESTION_KEY]
  if (typeof suggestion === 'string' && suggestion.trim() !== '') return suggestion
  const entries = Object.entries(counter.payload)
  if (entries.length === 0) return 'an alternative'
  return entries.map(([k, v]) => `${k}: ${String(v)}`).join(', ')
}

/**
 * A row can arrive decided with no `decision_reason` — an older row, a partial
 * sync, a server that resolved it without writing one. Falling through to the
 * raw status put the literal enum ("cancelled") on screen as if it were a
 * sentence, so the status carries its own words.
 */
function outcomeWithoutReason(status: ProposalStatus): string {
  switch (status) {
    case 'pending':
      return 'Waiting'
    case 'approved':
      return 'Approved'
    case 'rejected':
      return 'Rejected'
    case 'cancelled':
      return 'Withdrawn'
    case 'expired':
      return 'Expired'
  }
}

export function describeOutcome(status: ProposalStatus, reason: DecisionReason | null): string {
  switch (reason) {
    case 'unanimous_approval':
      return 'Approved — everyone said yes'
    case 'auto_approved_timeout':
      return 'Approved automatically — nobody objected before the deadline'
    case 'rejected_by_vote':
      return 'Rejected — one no is enough'
    case 'cancelled_by_proposer':
      return 'Withdrawn by whoever raised it'
    case 'expired':
      return 'Expired'
    case null:
      return outcomeWithoutReason(status)
  }
}

export function useProposalBoard(
  db: OfficeDb,
  clock: TrustedClock,
  me: MemberId,
  opts: UseProposalBoardOptions = {},
): { board: ProposalBoard; actions: ProposalActions } {
  const now = useNow(clock, opts.nowIntervalMs ?? 30_000)
  const settings = useSettings(db)
  const members = useMembers(db)
  const ctx = useMutationContext(db, clock, me)

  const proposalRows = useTable(db, 'proposals')
  const voteRows = useTable(db, 'proposal_votes')
  const typeRows = useTable(db, 'proposal_types')

  // `mutations.vote` / `cancelProposal` / `createProposal` append to the outbox
  // without touching the mirror, so the tap is acknowledged from React state
  // until the row syncs back. Components never write to Dexie directly.
  const [localVotes, setLocalVotes] = useState<readonly ProposalVote[]>([])
  const [localCancels, setLocalCancels] = useState<readonly string[]>([])
  const [localRaised, setLocalRaised] = useState<readonly string[]>([])

  const board = useMemo<ProposalBoard>(() => {
    const types = proposalTypesFromSettings(
      { autoApproveHours: settings.autoApproveHours },
      typeRows.map(toProposalType),
    )
    const eligible = members.filter(m => m.isOwner).map(m => m.id)
    const votes = [...voteRows.map(toProposalVote), ...localVotes]

    const settled = proposalRows.map(row => {
      const base = toProposal(row)
      const withCancel = localCancels.includes(String(base.id))
        ? (() => {
            const r = domainCancel(base, me, now, votes, eligible, { types })
            return r.ok ? r.proposal : base
          })()
        : base
      return settle(withCancel, votes, eligible, now, { types })
    })

    const view = (proposal: Proposal): ProposalView => {
      const type = types[proposal.typeKey] ?? null
      const summary = summarise(proposal, votes, eligible, now, { types })
      return {
        proposal,
        type,
        label: type?.label ?? proposal.typeKey,
        summary,
        mine: proposal.createdBy === me,
        awaitingMe: summary.awaiting.includes(me),
        votedByMe: summary.approvedBy.includes(me) || summary.rejectedBy.includes(me),
        eligibleToVote: eligible.includes(me),
        canCancel: domainCanCancel(proposal, me),
        money: type?.isMoneyRelated === true,
        counters: counterProposalsOf(proposal, votes),
        proposerName: nameOrYou(members, proposal.createdBy, me),
      }
    }

    const views = settled.map(view)

    const pending = views
      .filter(v => v.proposal.status === 'pending')
      // Waiting on you first, then oldest deadline, then oldest proposal.
      .sort(
        (a, b) =>
          Number(b.awaitingMe) - Number(a.awaitingMe) ||
          (a.proposal.autoApproveAt ?? Number.POSITIVE_INFINITY) -
            (b.proposal.autoApproveAt ?? Number.POSITIVE_INFINITY) ||
          a.proposal.createdAt - b.proposal.createdAt,
      )

    const decided = views
      .filter(v => v.proposal.status !== 'pending')
      .sort((a, b) => (b.proposal.decidedAt ?? 0) - (a.proposal.decidedAt ?? 0))

    const relaunchable: Relaunchable[] = []
    for (const v of decided) {
      if (!v.mine || v.proposal.status !== 'rejected') continue
      if (localRaised.includes(String(v.proposal.id))) continue
      if (supersededBy(settled, v.proposal.id) !== null) continue
      const counter = v.counters[0]
      if (counter === undefined) continue
      relaunchable.push({ view: v, counter, counterFrom: nameOf(members, counter.voterId) })
    }

    return {
      now,
      rolloverHour: settings.dayRolloverHour,
      members,
      eligible,
      pending,
      decided,
      relaunchable,
      log: decisionLog(settled, votes, { eligibleVoters: eligible }),
      types,
    }
  }, [proposalRows, voteRows, typeRows, members, settings, me, now, localVotes, localCancels, localRaised])

  const castVote = useCallback(
    async (
      view: ProposalView,
      vote: Vote,
      comment: string | null,
      counter: Record<string, unknown> | null,
    ) => {
      const at = clock.now()
      await mutations.vote(
        ctx,
        String(view.proposal.id),
        vote,
        comment ?? undefined,
        counter ?? undefined,
      )
      setLocalVotes(prev => [
        ...prev,
        {
          proposalId: view.proposal.id,
          voterId: me,
          vote,
          comment,
          counterProposal: counter,
          votedAt: at,
        },
      ])
    },
    [ctx, clock, me],
  )

  const actions = useMemo<ProposalActions>(
    () => ({
      approve: view => castVote(view, 'approve', null, null),
      rejectWithAlternative: (view, suggestion, note) =>
        castVote(view, 'reject', note.trim() === '' ? null : note.trim(), {
          [COUNTER_SUGGESTION_KEY]: suggestion.trim(),
        }),
      rejectWithReason: (view, reason) => castVote(view, 'reject', reason.trim(), null),
      cancel: async view => {
        await mutations.cancelProposal(ctx, String(view.proposal.id))
        setLocalCancels(prev =>
          prev.includes(String(view.proposal.id)) ? prev : [...prev, String(view.proposal.id)],
        )
      },
      raise: async (view, counter) => {
        const type = view.type
        if (type === null) return
        const at = clock.now()
        // The domain builds the superseding proposal — merged payload, fresh
        // deadline, `supersedesId` pointing back at the original — so the
        // decision log stays honest about what was proposed and what was agreed.
        const next = acceptCounterProposal({
          original: view.proposal,
          counter,
          id: uuidv7(at) as ProposalId,
          createdBy: me,
          now: at,
          type,
        })
        await mutations.createProposal(ctx, {
          typeKey: next.typeKey,
          title: next.title,
          ...(next.details === null ? {} : { details: next.details }),
          payload: { ...next.payload },
          // The link is a top-level field, which is where `sync_push` reads it
          // from. Sent inside `payload` it reached the server and was silently
          // dropped: `supersedes_id` stayed null, the chain the decision log
          // walks was broken on every device but the one that raised it, and
          // this screen went on offering "Raise this instead" for a proposal
          // that had already been re-raised.
          supersedesId: String(next.supersedesId ?? view.proposal.id),
          autoApproveAt:
            next.autoApproveAt === null ? null : new Date(next.autoApproveAt).toISOString(),
        })
        setLocalRaised(prev =>
          prev.includes(String(view.proposal.id)) ? prev : [...prev, String(view.proposal.id)],
        )
      },
    }),
    [castVote, ctx, clock, me],
  )

  return { board, actions }
}
